import type { Address, PublicClient } from "viem"
import { useState } from "react"
import { useParams } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { useConfig } from "wagmi"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { ShowWhenTrue } from "@/components/conditionals"
import { InnerCard } from "@/components/inner-card"
import { InnerCardWarning } from "@/components/inner-card-warning"
import { Separator } from "@/components/separator"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { ClFarmStore } from "@/domains/cl-farm"
import { AlmProviderImageLink } from "@/domains/cl-farms-page/components/alm-provider-image-link"
import { ClFarmsPageLayout } from "@/domains/cl-farms-page/components/cl-farms-page-layout"
import { ClFarmsSubHeader } from "@/domains/cl-farms-page/components/cl-farms-sub-header"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { BodyBase } from "@/domains/design/components/body-base"
import { BodyLg } from "@/domains/design/components/body-lg"
import { DollarIcon, DownArrowIcon, WalletIcon } from "@/domains/icons"
import {
  redeemClFarmLiquidityFromGuardInteraction,
  redeemClFarmLiquidityFromVaultInteraction,
} from "@/domains/interactions"
import { cn, dollars } from "@/domains/library"
import { ClFarmsPagesComplexBreadcrumbApplication } from "@/domains/navigation/components/complex-breadcrumb-applications/cl-farms-pages-complex-breadcrumb-application"
import { ClFarmsLiquiditySubItem } from "@/domains/navigation/subitems/clfarms-liquidity-subitem"
import { TokenStore } from "@/domains/tokens"
import { TokenIcon } from "@/domains/tokens/components/token-icon"
import { ITokenInputBalance, TokenInputBalance } from "@/domains/tokens/components/token-input-balance"
import { TokenInputValue } from "@/domains/tokens/components/token-input-value"
import { ITokenPercentageButtons, TokenPercentageButtons } from "@/domains/tokens/components/token-percentage-buttons"
import { INSUFFICIENT_BALANCE_ERROR } from "@/features/liquidity/constants"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useClFarm } from "@/hooks/use-cl-farm"
import useEventEmitter from "@/hooks/useEventEmitter"

const BREADCRUMB_ITEMS = [new ClFarmsLiquiditySubItem(), ClFarmsPagesComplexBreadcrumbApplication]

const CLFarmsRedeem = observer(() => {
  const { address: clFarmAddressParam } = useParams<{ address: string }>()
  const config = useConfig()
  const emitter = useEventEmitter()
  const chainConfig = useChainConfig()
  const scaleApp = useScaleApplication()

  const clFarmAddress = clFarmAddressParam ? (clFarmAddressParam as Address) : undefined

  const clFarm = useClFarm(clFarmAddress)
  const [amount, setAmount] = useState<string>("0")
  const amountBI = clFarm?.parseUnits(amount) ?? 0n

  const redeemAction = async () => {
    if (!clFarm) return

    const chainId = chainConfig.chainId

    if (!scaleApp.Wallet.Connection.isConnected) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }
    if (!scaleApp.Wallet.Connection.connectedAddress) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }

    const publicClient = scaleApp.getPublicClient()
    if (clFarm.alm === "Ichi") {
      const result = await redeemClFarmLiquidityFromGuardInteraction({
        amount: amountBI,
        approvalMode: scaleApp?.Settings.approvalMode,
        chainId,
        clFarm,
        config,
        connectedAddress: scaleApp.Wallet.Connection.connectedAddress.toViemAddress(),
        emitter,
        publicClient,
        onSuccess: () => {
          scaleApp.Log.info(clFarm.alm, "onSuccess", result)
          setAmount("0")
          scaleApp.Log.info("ClFarms.Orchestrator.userInfoQueryInstance.forceExecute")
          scaleApp.ClFarms.Orchestrator.userInfoQueryInstance?.forceExecute()

          // the token should update it's balance
          scaleApp.Log.info("Tokens.Orchestrator.balanceQuery.forceExecute")
          scaleApp.Tokens.Orchestrator.balanceQuery?.forceExecute()
        },
      })
      console.log(clFarm.alm, "result", result)
    }
    if (clFarm.alm === "Steer" || clFarm.alm === "Fertilizer" || clFarm.alm === "Gamma") {
      const result = await redeemClFarmLiquidityFromVaultInteraction({
        amount: amountBI,
        approvalMode: scaleApp?.Settings.approvalMode,
        chainId,
        clFarm,
        config,
        connectedAddress: scaleApp.Wallet.Connection.connectedAddress.toViemAddress(),
        emitter,
        publicClient,
        onSuccess: () => {
          setAmount("0")
          scaleApp.ClFarms.Orchestrator.userInfoQueryInstance?.forceExecute()

          // the token should update it's balance
          scaleApp.Tokens.Orchestrator.balanceQuery?.forceExecute()
        },
      })
      console.log(clFarm.alm, "result", result)
    }
  }

  const title = clFarm ? `Redeem ${clFarm.displayName} CL Farms Liquidity` : "Redeem CL Farms Liquidity"
  const subTitle = "back into your individual tokens"

  return (
    <TooltipProvider>
      <PageContainer>
        <MetaData title={title} />
        <PageTitle title="Redeem CL Farms Liquidity" subTitle={subTitle} items={BREADCRUMB_ITEMS} />
        <ClFarmsPageLayout
          clFarmAddress={clFarmAddress}
          clFarm={clFarm}
          leftColumn={
            <RedeemClFarm clFarm={clFarm} amount={amount} setAmount={setAmount} redeemAction={redeemAction} />
          }
        />
      </PageContainer>
    </TooltipProvider>
  )
})

export default CLFarmsRedeem

type RedeemClFarmProps = {
  clFarm: ClFarmStore | null | undefined
  amount: string
  setAmount: (v: string) => void
  redeemAction: () => void
}

const RedeemClFarm = observer(({ clFarm, amount, setAmount, redeemAction }: RedeemClFarmProps) => {
  if (!clFarm) return null

  const buttonsToken: ITokenPercentageButtons & ITokenInputBalance = {
    address: clFarm.address,
    symbol: clFarm.symbol ?? "unknown",
    imageURL: clFarm.imageURL ?? "",
    decimals: clFarm.decimals ?? 18,
    displayBalance: clFarm.formatUnits(clFarm.onChainUserBalance ?? 0n),
    calculateValue: (percentage: number): string => {
      return clFarm?.calculateValuePercentage(clFarm.onChainUserBalance, percentage) ?? "0"
    },
  }

  const amountBI = clFarm.parseUnits(amount)

  const hasSufficientBalance = amountBI <= clFarm.onChainUserBalance

  const withdrawAmountUsd = clFarm?.calculateValueUSD(Number(amount)) ?? 0
  // what % of total supply is the amount?
  const percentageOfTotalSupply =
    clFarm.onChainTotalSupply > 0n ? Number(amount) / clFarm.formatUnitsAsNumber(clFarm.onChainTotalSupply) : 0

  const token0Receive: string =
    clFarm.onChainTotalSupply > 0n
      ? clFarm.token0.calculateValuePercentage(clFarm.onChainReserve0, percentageOfTotalSupply)
      : "0"

  const token0ReceiveDisplayValue = Number(token0Receive) > 0 ? token0Receive : "0.00"

  const token1Receive: string =
    clFarm.onChainTotalSupply > 0n
      ? clFarm.token1.calculateValuePercentage(clFarm.onChainReserve1, percentageOfTotalSupply)
      : "0"

  const token1ReceiveDisplayValue = Number(token1Receive) > 0 ? token1Receive : "0.00"

  const canInteract = amountBI > 0n && hasSufficientBalance

  return (
    <Card.Container className="flex w-full min-w-[320px] flex-col gap-6 rounded-lg border border-p400-light bg-p400 p-4 lg:p-6">
      <ShowWhenTrue when={!!clFarm}>
        <ClFarmsSubHeader
          displayName={clFarm.displayName ?? ""}
          tags={clFarm.tags ?? []}
          description={clFarm.description ?? ""}
        />
        <InnerCard hasWarning={!hasSufficientBalance}>
          <div className="flex flex-row items-center justify-between">
            <TokenInputValue
              value={amount}
              token={buttonsToken}
              onValueChange={(v) => setAmount(v)}
              className="h-fit min-w-[180px] flex-grow p-0"
              placeholder="0"
            />
          </div>

          {/* balances */}
          <div className="flex w-full flex-row items-center justify-between text-xs">
            <ShowWhenTrue when={hasSufficientBalance}>
              <div className="flex items-center gap-1 text-sm text-t200">
                <DollarIcon size={14} className="text-t200" />
                <p>~{dollars(withdrawAmountUsd)}</p>
              </div>
            </ShowWhenTrue>
            <ShowWhenTrue when={!hasSufficientBalance}>
              <InnerCardWarning warning={INSUFFICIENT_BALANCE_ERROR} />
            </ShowWhenTrue>
            <Tooltip key={"balance"}>
              <TooltipTrigger asChild>
                <TokenInputBalance token={buttonsToken} label={<WalletIcon size={14} className="text-t200" />} />
              </TooltipTrigger>
              <TooltipContent>
                <div>{clFarm?.formatUnits(clFarm?.onChainUserGaugeBalance ?? 0n)}</div>
              </TooltipContent>
            </Tooltip>
          </div>

          <TokenPercentageButtons token={buttonsToken} onClick={setAmount} />
        </InnerCard>
        <div className="flex cursor-default flex-row items-center justify-center">
          <DownArrowIcon className="h-5 w-5 text-t200" />
        </div>
        <div className="flex flex-col">
          <TokenOverview token={clFarm.token0} displayValue={token0ReceiveDisplayValue} />
          <div className="py-4">
            <Separator
              className="relative h-0 w-full overflow-visible rounded-md border border-solid border-p400-light
              after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] after:rounded-md 
              after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']"
              decorative
            />
          </div>
          <TokenOverview token={clFarm.token1} displayValue={token1ReceiveDisplayValue} />
        </div>
        <div className="my-2 flex w-full justify-end">
          <Button disabled={!canInteract} variant={"primary"} onClick={redeemAction} size="fit">
            Redeem
          </Button>
        </div>
      </ShowWhenTrue>
    </Card.Container>
  )
})

const TokenOverview = ({ token, displayValue }: { token: TokenStore; displayValue: string }) => {
  return (
    <div className="flex justify-between">
      <div className="flex flex-row items-center gap-1.5">
        <TokenIcon token={token} size={20} />
        <BodyBase As="span">{token.symbol}</BodyBase>
      </div>
      <BodyBase As="span" className={cn(displayValue === "0.00" ? "text-t200" : "")}>
        {displayValue}
      </BodyBase>
    </div>
  )
}
